import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { Switch } from "@/components/ui/switch";
import {
  updateRegistrantAccountUserInspectionAccess,
  updateRegistrantLoginIdentityInspectionAccess,
} from "@/hera-api/client";
import type { RegistrantAccountUserResponseSchema } from "@/hera-api/client/types.gen";
import { invalidateRegistrantUserQueries } from "@/lib/registrant-user-query-invalidation";
import { getQueryErrorMessage } from "@/lib/utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";

interface UserPortalTogglesProps {
  row: RegistrantAccountUserResponseSchema;
  disabled?: boolean;
  primaryAccountId?: string;
  idPrefix?: string;
  fullLabels?: boolean;
  onRowChange?: (row: RegistrantAccountUserResponseSchema) => void;
}

export function UserPortalToggles({
  row,
  disabled = false,
  primaryAccountId,
  idPrefix = "",
  fullLabels = false,
  onRowChange,
}: UserPortalTogglesProps) {
  const queryClient = useQueryClient();
  const loginIdentities = row.login_identities ?? [];
  const rowLocked = disabled || row.status === "disabled";

  const inspectionAccessMutation = useMutation({
    mutationFn: ({
      loginIdentityId,
      body,
    }: {
      loginIdentityId: string;
      body: {
        inspection_only?: boolean;
        license_access_mode?: "ALL" | "SELECTED";
      };
    }) =>
      updateRegistrantLoginIdentityInspectionAccess({
        path: { login_identity_id: loginIdentityId },
        body,
      }).then((res) => {
        if (res.error) {
          throw new Error(getQueryErrorMessage(res.error));
        }
      }),
    onSuccess: () => {
      invalidateRegistrantUserQueries(queryClient);
      toast({ title: "Saved", description: "Portal access updated." });
    },
    onError: (err: Error) => {
      toast({
        title: "Error",
        description: err.message || "Could not update portal access",
        variant: "destructive",
      });
    },
  });

  if (loginIdentities.length === 0) {
    return (
      <PreLoginPortalToggles
        row={row}
        idPrefix={idPrefix}
        fullLabels={fullLabels}
        // Pending is the normal state here: the point is to choose access
        // before the member signs up. Only disabled members are locked.
        disabled={disabled || row.status === "disabled"}
        onRowChange={onRowChange}
      />
    );
  }

  return (
    <div
      className="flex flex-col gap-2 py-1 whitespace-normal"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      {loginIdentities.map((li) => {
        const isPrimaryIdentity = li.registrant_account_id === primaryAccountId;
        const registrationSwitchId = `${idPrefix}registration-portal-${li.id}`;
        const inspectionSwitchId = `${idPrefix}inspection-portal-${li.id}`;
        return (
          <div key={li.id} className="flex flex-col gap-2">
            {loginIdentities.length > 1 ? (
              <p className="text-xs text-muted-foreground truncate">
                {li.email}
              </p>
            ) : null}
            <div className="flex items-center gap-2 text-sm">
              <Switch
                id={registrationSwitchId}
                checked={!li.inspection_only}
                disabled={
                  rowLocked ||
                  (!onRowChange && inspectionAccessMutation.isPending) ||
                  isPrimaryIdentity
                }
                onCheckedChange={(checked) => {
                  if (onRowChange) {
                    onRowChange({
                      ...row,
                      login_identities: loginIdentities.map((identity) =>
                        identity.id === li.id
                          ? { ...identity, inspection_only: !checked }
                          : identity,
                      ),
                    });
                    return;
                  }
                  inspectionAccessMutation.mutate({
                    loginIdentityId: li.id,
                    body: { inspection_only: !checked },
                  });
                }}
                aria-label={`Registration Portal ${li.email}`}
              />
              <Label htmlFor={registrationSwitchId} className="font-normal">
                {fullLabels ? "Registration Portal" : "Registration"}
              </Label>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Switch
                id={inspectionSwitchId}
                checked={li.license_access_mode === "ALL"}
                disabled={
                  rowLocked ||
                  (!onRowChange && inspectionAccessMutation.isPending) ||
                  (isPrimaryIdentity && !li.inspection_only)
                }
                onCheckedChange={(checked) => {
                  if (onRowChange) {
                    onRowChange({
                      ...row,
                      login_identities: loginIdentities.map((identity) =>
                        identity.id === li.id
                          ? {
                              ...identity,
                              license_access_mode: checked ? "ALL" : "SELECTED",
                            }
                          : identity,
                      ),
                    });
                    return;
                  }
                  inspectionAccessMutation.mutate({
                    loginIdentityId: li.id,
                    body: {
                      license_access_mode: checked ? "ALL" : "SELECTED",
                    },
                  });
                }}
                aria-label={`Inspection Portal ${li.email}`}
              />
              <Label htmlFor={inspectionSwitchId} className="font-normal">
                {fullLabels ? "Inspection Portal" : "Inspection"}
              </Label>
            </div>
          </div>
        );
      })}
    </div>
  );
}

interface PreLoginPortalTogglesProps {
  row: RegistrantAccountUserResponseSchema;
  idPrefix: string;
  fullLabels: boolean;
  disabled: boolean;
  onRowChange?: (row: RegistrantAccountUserResponseSchema) => void;
}

/**
 * Portal access for an org member with no registrant login yet: imported, or
 * invited and never signed up. The choice is staged on the account and copied
 * onto their login identity the first time they log in.
 */
function PreLoginPortalToggles({
  row,
  idPrefix,
  fullLabels,
  disabled,
  onRowChange,
}: PreLoginPortalTogglesProps) {
  const queryClient = useQueryClient();
  // Nothing staged means they get the defaults a fresh login identity gets:
  // registrant portal on, all-inspections off.
  const inspectionOnly = row.inspection_only_before_login ?? false;
  const allInspections = row.license_access_mode_before_login === "ALL";

  const stageMutation = useMutation({
    mutationFn: (body: {
      inspection_only?: boolean;
      license_access_mode?: "ALL" | "SELECTED";
    }) =>
      updateRegistrantAccountUserInspectionAccess({
        path: { user_id: row.id },
        body,
      }).then((res) => {
        if (res.error) {
          throw new Error(getQueryErrorMessage(res.error));
        }
      }),
    onSuccess: () => {
      invalidateRegistrantUserQueries(queryClient);
      toast({
        title: "Saved",
        description: "Portal access will apply when this user first logs in.",
      });
    },
    onError: (err: Error) => {
      toast({
        title: "Error",
        description: err.message || "Could not update portal access",
        variant: "destructive",
      });
    },
  });

  const stage = (body: {
    inspection_only?: boolean;
    license_access_mode?: "ALL" | "SELECTED";
  }) => {
    if (onRowChange) {
      onRowChange({
        ...row,
        ...(body.inspection_only === undefined
          ? {}
          : { inspection_only_before_login: body.inspection_only }),
        ...(body.license_access_mode === undefined
          ? {}
          : { license_access_mode_before_login: body.license_access_mode }),
      });
      return;
    }
    stageMutation.mutate(body);
  };

  const registrationSwitchId = `${idPrefix}registration-portal-${row.id}`;
  const inspectionSwitchId = `${idPrefix}inspection-portal-${row.id}`;
  const switchesDisabled =
    disabled || (!onRowChange && stageMutation.isPending);

  return (
    <div
      className="flex flex-col gap-2 py-1 whitespace-normal"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <div className="flex items-center gap-2 text-sm">
        <Switch
          id={registrationSwitchId}
          checked={!inspectionOnly}
          disabled={switchesDisabled}
          onCheckedChange={(checked) => stage({ inspection_only: !checked })}
          aria-label={`Registration Portal ${row.email}`}
        />
        <Label htmlFor={registrationSwitchId} className="font-normal">
          {fullLabels ? "Registration Portal" : "Registration"}
        </Label>
      </div>
      <div className="flex items-center gap-2 text-sm">
        <Switch
          id={inspectionSwitchId}
          checked={allInspections}
          disabled={switchesDisabled}
          onCheckedChange={(checked) =>
            stage({ license_access_mode: checked ? "ALL" : "SELECTED" })
          }
          aria-label={`Inspection Portal ${row.email}`}
        />
        <Label htmlFor={inspectionSwitchId} className="font-normal">
          {fullLabels ? "Inspection Portal" : "Inspection"}
        </Label>
      </div>
      <p className="text-xs text-muted-foreground">
        Applies when this user first logs in.
      </p>
    </div>
  );
}
